/**
 * Worker-side host transport for `renderMode: remote-runtime` extensions
 * (Contract B). Owns the lifecycle of:
 *
 *   1. A path-pinned module `Worker` spawned from a host-issued bootstrap URL.
 *   2. A `MessagePort`-based RPC channel (handed to the worker on spawn).
 *   3. A Shopify Remote DOM root receiver — wired here so the worker's UI
 *      tree mounts into a host React tree.
 *   4. Event coalescing (default ≤16ms, one rAF) for high-frequency input
 *      events before they cross the port.
 *   5. MCP tool/resource calls forwarded through the port — but **the
 *      capability token never crosses the boundary**. The token is bound
 *      to the worker scope on the HOST side: the host transport intercepts
 *      the plugin's MCP request, attaches the token to the outbound HTTP
 *      call, and forwards ONLY the response payload back through the port.
 *
 * This module is intentionally framework-thin: callers wire `mount(root)`
 * onto a React tree (typically inside a `<WorkerSurfaceMount>`), and the
 * Remote DOM receiver is responsible for translating worker-side
 * RemoteRoot mutations into host-side component renders.
 */

import { createOffscreenCanvasTransfer } from "./offscreen";
import { classifyHostError, classifyHostResponse } from "../../bridge/mcp-error";
import type { UploadDocumentMeta } from "../../plugin/transport";
import {
  detectVersionSkew,
  type McpHttpResponse,
  type VersionSkew,
} from "../version-skew";
import {
  BRIDGE_PUSH_THEME,
  BRIDGE_PUSH_LOCALE,
  BRIDGE_PUSH_DENSITY,
  BRIDGE_PUSH_A11Y,
  BRIDGE_NAV_PUSH,
  BRIDGE_CHROME_REQUEST,
  BRIDGE_A11Y_ANNOUNCE,
  BRIDGE_LIFECYCLE_EVENT,
  type BridgePushThemeEnvelope,
  type BridgePushLocaleEnvelope,
  type BridgePushDensityEnvelope,
  type BridgePushA11yEnvelope,
  type BridgeNavPushEnvelope,
} from "./bridge-envelopes";

// ─── Public types ─────────────────────────────────────────────────────────────

/**
 * Structural subset of the DOM `Worker` interface that the transport needs.
 * Tests inject a fake; production callers pass the global `Worker`.
 */
export interface WorkerLike
{
    onmessage: ((ev: MessageEvent) => void) | null;
    onerror: ((ev: ErrorEvent) => void) | null;
    onmessageerror: ((ev: MessageEvent) => void) | null;
    postMessage(message: unknown, transfer?: Transferable[]): void;
    terminate(): void;
}

/**
 * Constructor signature compatible with the DOM `Worker` constructor. The
 * `workerCtor` option exists exclusively so tests can swap in a fake — in
 * production the caller passes the global `Worker`.
 */
export type WorkerCtor = new (url: string | URL, options?: WorkerOptions) => WorkerLike;

/**
 * Discriminated union of MCP fetch shapes the host transport issues. The
 * `capabilityToken` is attached on the host side ONLY — it is never present
 * in any message that crosses the worker port.
 */
export type McpHttpRequest =
    | {
        kind: "invokeTool";
        name: string;
        args: unknown;
        capabilityToken: string;
        signal?: AbortSignal;
    }
    | {
        kind: "getResource";
        uri: string;
        capabilityToken: string;
        signal?: AbortSignal;
    }
    | McpUploadRequest;

/**
 * Host-side document-upload request (a `kind` of {@link McpHttpRequest}).
 * Carries the transferred bytes + advisory metadata plus the host-minted
 * capability token. As with the other request kinds, the token is attached on
 * the HOST side only and never crosses the worker port. Implementations POST
 * the bytes as `application/octet-stream` (metadata in `x-cc-*` headers) to the
 * kernel's capability-token-authed document-upload route; `data` on success is
 * an {@link UploadDocumentResult}.
 */
export interface McpUploadRequest
{
    kind: "uploadDocument";
    meta: UploadDocumentMeta;
    buffer: ArrayBuffer;
    capabilityToken: string;
    signal?: AbortSignal;
}

/**
 * Host-side HTTP client for MCP calls. Implementations are responsible for
 * carrying the supplied `capabilityToken` on the outbound request (typically
 * via `Authorization: Bearer` / `PluginCap`). The `uploadDocument` kind is
 * additive — implementations that predate it fall through to their unknown-kind
 * branch until updated (the FE upload hook is inert until the host handles it).
 */
export interface McpHttpClient
{
    /**
     * Performs the call. The response's `status` and `headers` are optional and additive: supplying
     * them is what enables version-skew detection (see {@link detectVersionSkew}), and an
     * implementation that omits them behaves exactly as before rather than failing.
     */
    fetch(request: McpHttpRequest): Promise<McpHttpResponse>;
}

/**
 * Construction options for {@link WorkerRemoteDomTransport}.
 */
export interface WorkerRemoteDomTransportOptions
{
    /**
     * Canonical host-origin URL for the worker bootstrap script. This MUST
     * be supplied by the host — never built from user input or extension
     * manifest values — so the worker spawn is path-pinned.
     */
    bootstrapUrl: string;

    /**
     * Mints (or fetches a cached) capability token. The host transport
     * resolves this for each outbound MCP HTTP call. The token NEVER leaves
     * host scope — plugin code (the worker side) cannot read it.
     */
    capabilityToken: () => Promise<string>;

    /**
     * Host-side MCP HTTP client. The transport bridges port-side plugin
     * MCP requests to this client and returns only the response payload
     * back through the port.
     */
    mcpClient: McpHttpClient;

    /**
     * The extension version this surface mounted at. Supplying it enables *proactive* skew
     * detection: a successful response stamped with a different version means the install moved
     * while the surface is still working, so it can remount before anything fails. Without it only
     * outright rejections are detectable, which is a worse experience but not a broken one.
     */
    mountedExtensionVersion?: string;

    /**
     * Called at most once, when this surface is found to be running against a version that is no
     * longer installed. The host is expected to remount the surface (which re-mints a capability
     * token against the new version); the transport itself does not retry, because the pending call
     * belongs to a page that is about to be replaced.
     */
    onVersionSkew?: (skew: VersionSkew) => void;

    /**
     * Injectable Worker constructor (tests use a fake). Defaults to the
     * global `Worker`.
     */
    workerCtor?: WorkerCtor;

    /**
     * Coalescing window in milliseconds for high-frequency events. Defaults
     * to 16ms (~one animation frame). Trailing-edge: the most recent payload
     * within the window is delivered after the window elapses.
     */
    coalesceMs?: number;

    /**
     * Maximum number of MCP requests (combined `invokeTool` + `getResource`)
     * the transport will forward to {@link mcpClient} concurrently. Defaults
     * to 8. Requests above the cap are rejected with a deterministic error
     * reply over the port — the worker MUST handle that response shape.
     *
     * Bounded concurrency is a back-pressure boundary, not a quota: it stops
     * a compromised or buggy worker from saturating the host's HTTP / token
     * pool. Production hosts may want to lower this in multi-tenant pools
     * where many extensions share one host process.
     */
    maxConcurrentMcpRequests?: number;
}

/**
 * Wire shape of a host-sourced input event forwarded to the worker over the
 * port. Pointer / wheel / keyboard payloads share a single envelope type so
 * the worker has one inbound dispatch site. The transport is structurally
 * agnostic to the payload — it forwards verbatim.
 *
 * `kind` discriminates the event family. Coordinates are CSS pixels relative
 * to the host canvas; coalescing happens host-side before the call to
 * {@link WorkerRemoteDomTransport.postInputEvent}.
 */
export type InputEventPayload =
    | {
        kind: "pointermove" | "pointerdown" | "pointerup" | "pointercancel";
        surfaceId: string;
        x: number;
        y: number;
        buttons: number;
        pointerType: string;
    }
    | {
        kind: "wheel";
        surfaceId: string;
        deltaX: number;
        deltaY: number;
        deltaMode: number;
    }
    | {
        kind: "keydown" | "keyup";
        surfaceId: string;
        key: string;
        code: string;
        ctrlKey: boolean;
        shiftKey: boolean;
        altKey: boolean;
        metaKey: boolean;
    };

/**
 * Documented wire protocol identifier embedded in handshake / message
 * envelopes. Bumped when the worker ↔ host message shape changes in a
 * backward-incompatible way.
 */
export const WORKER_TRANSPORT_PROTOCOL = "ethisys.worker.remotedom.v1";

/**
 * Wire shape of the handshake message the host posts to the worker on
 * {@link WorkerRemoteDomTransport.connect}. Locked here so the SDK side and the
 * API-hosted bootstrap script (`WorkerBootstrapScriptProvider`) read from the
 * same field names — see the API tests pinning `event.data.moduleUrl` and
 * `event.data.importMap`.
 *
 * The handshake is the SOLE message that ever carries `moduleUrl` or
 * `importMap`; subsequent port traffic uses the discriminated message types
 * declared below. The capability token NEVER appears in this payload (or any
 * other postMessage payload) — it is bound on the host side via
 * {@link WorkerRemoteDomTransportOptions.capabilityToken}.
 */
export interface WorkerHandshakePayload
{
    readonly type: "ethisys:worker:handshake";
    readonly protocol: string;
    /** Host-origin URL of the plugin's worker bundle entry. */
    readonly moduleUrl: string;
    /**
     * Frozen bare-specifier → host-origin URL map that the bootstrap installs
     * before importing {@link moduleUrl}. Pulled from the plugin's
     * `worker-bundle.import-map.json` at runtime by the host mount.
     */
    readonly importMap: Readonly<Record<string, string>>;
}

// ─── Implementation ───────────────────────────────────────────────────────────

interface InvokeToolMessage
{
    id: string;
    type: "ethisys:mcp:invokeTool";
    name: string;
    args: unknown;
}

interface GetResourceMessage
{
    id: string;
    type: "ethisys:mcp:getResource";
    uri: string;
}

interface UploadDocumentMessage
{
    id: string;
    type: "ethisys:mcp:uploadDocument";
    meta: UploadDocumentMeta;
    buffer: ArrayBuffer;
}

interface RemoteDomMessage
{
    type: "ethisys:remotedom";
    payload: unknown;
}

type InboundMessage = InvokeToolMessage | GetResourceMessage | UploadDocumentMessage | RemoteDomMessage | { type: string; [k: string]: unknown };

/**
 * Worker-side host transport for Contract B extensions.
 *
 * Construction immediately spawns the worker and transfers one end of a
 * fresh `MessageChannel`; the host retains `port1`, the worker receives
 * `port2`. Both sides exchange messages via their port and the worker port
 * is the *only* channel for plugin ↔ host traffic after handshake.
 */
/**
 * Default cap for in-flight MCP requests forwarded by the transport. Chosen to
 * cover typical interactive UI traffic (dashboards, list views, detail panels)
 * without letting a runaway worker saturate the host's HTTP / token pool.
 */
export const DEFAULT_MAX_CONCURRENT_MCP_REQUESTS = 8;

export class WorkerRemoteDomTransport
{
    private readonly worker: WorkerLike;
    private readonly hostPort: MessagePort;
    private readonly workerPort: MessagePort;
    private readonly mcpClient: McpHttpClient;
    private readonly capabilityTokenProvider: () => Promise<string>;
    private readonly mountedExtensionVersion?: string;
    private readonly onVersionSkew?: (skew: VersionSkew) => void;
    private versionSkewReported = false;
    private readonly coalesceMs: number;
    private readonly maxConcurrentMcpRequests: number;
    private readonly abortController: AbortController;
    // Per-request AbortControllers keyed by request id. An inbound
    // `ethisys:mcp:abort` envelope (the worker posts one when a hook's
    // AbortSignal fires on unmount / arg change) aborts just that request;
    // dispose() fans out to every registered controller. Without this a
    // cancelled call — most costly for uploadDocument — keeps streaming to the
    // kernel even though the plugin promise already rejected.
    private readonly inFlightAborts = new Map<string, AbortController>();
    private inFlightMcpRequests = 0;
    private remoteDomConsumer: ((payload: unknown) => void) | undefined;
    private bridgeMessageConsumer: ((msg: unknown) => void) | undefined;
    private disposed = false;
    private connected = false;

    public constructor(options: WorkerRemoteDomTransportOptions)
    {
        const WorkerCtor = options.workerCtor ?? (globalThis as unknown as { Worker: WorkerCtor }).Worker;
        if (!WorkerCtor)
        {
            throw new Error("WorkerRemoteDomTransport: no Worker constructor available");
        }

        this.mcpClient = options.mcpClient;
        this.capabilityTokenProvider = options.capabilityToken;
        this.mountedExtensionVersion = options.mountedExtensionVersion;
        this.onVersionSkew = options.onVersionSkew;
        this.coalesceMs = options.coalesceMs ?? 16;
        this.maxConcurrentMcpRequests = Math.max(
            1,
            options.maxConcurrentMcpRequests ?? DEFAULT_MAX_CONCURRENT_MCP_REQUESTS,
        );
        // Aborted from dispose() — propagates into the mcpClient.fetch path so
        // already-in-flight HTTP calls can short-circuit instead of resolving
        // into a closed port.
        this.abortController = new AbortController();

        // Spawn the worker FIRST so the constructor's spawn shape is
        // observable to tests even if MessageChannel construction throws.
        this.worker = new WorkerCtor(options.bootstrapUrl, { type: "module" });

        const channel = new MessageChannel();
        this.hostPort = channel.port1;
        this.workerPort = channel.port2;

        // Wire host-side port handling before {@link connect} posts the
        // handshake — this closes a race where the worker could reply faster
        // than we wire up.
        this.hostPort.onmessage = (ev) => this.handlePortMessage(ev.data as InboundMessage);
        this.hostPort.start();

        this.worker.onerror = (ev) =>
        {
            // Defensive: do NOT echo worker errors back to the worker — they
            // may carry plugin-side details. Host-side observability is the
            // platform's responsibility.
            void ev;
        };
    }

    /**
     * Post the handshake to the worker. Idempotent — only the FIRST call
     * transfers the {@link MessagePort} and posts the handshake payload;
     * subsequent calls are silent no-ops. Splitting this off from the
     * constructor lets the host mount fetch the per-plugin
     * `worker-bundle.import-map.json` (and resolve the bundle's module URL)
     * before the bootstrap script consumes them.
     *
     * Wire shape: {@link WorkerHandshakePayload}. The capability token NEVER
     * appears in the payload — it's bound on the host side via the
     * {@link WorkerRemoteDomTransportOptions.capabilityToken} provider.
     *
     * The `moduleUrl` and `importMap` are forwarded to the worker so the
     * bootstrap script (served from the host-pinned
     * `/extensions/runtime/worker-bootstrap.js`) can:
     *   1. Compose the frozen, same-origin-validated `IMPORT_MAP` from the
     *      handshake payload (rejecting any cross-origin entries).
     *   2. `safeImport(moduleUrl)` the plugin's entry module.
     *
     * Same-origin enforcement is the bootstrap's job — this transport is
     * structurally agnostic to the host origin and only forwards what it's
     * handed.
     */
    public connect(moduleUrl: string, importMap: Record<string, string>): void
    {
        if (this.connected)
        {
            return;
        }
        this.connected = true;

        // Snapshot the import map into a fresh object so the worker handshake
        // is decoupled from caller-side mutation. `Object.freeze` here is
        // defence-in-depth — the worker rebuilds with `Object.create(null)`
        // anyway, but freezing the host-side payload makes mutation a typed
        // error for any host code that hangs onto the reference.
        const handshake: WorkerHandshakePayload = Object.freeze({
            type: "ethisys:worker:handshake",
            protocol: WORKER_TRANSPORT_PROTOCOL,
            moduleUrl,
            importMap: Object.freeze({ ...importMap }),
        });
        this.worker.postMessage(handshake, [this.workerPort]);
    }

    /**
     * Bind a consumer for Remote DOM mutation payloads emitted by the worker.
     * The Remote DOM receiver wiring is owned by the caller (typically a host
     * React component); this method exposes the raw stream so the receiver
     * can integrate cleanly without a circular package import.
     */
    public onRemoteDom(consumer: (payload: unknown) => void): void
    {
        this.remoteDomConsumer = consumer;
    }

    /**
     * Transfer control of a host-owned `<canvas>` to the worker so plugin code
     * can render via `OffscreenCanvas`. The host retains the `<canvas>` for
     * layout / accessibility purposes only — every pixel is produced inside the
     * worker, so the main thread is never blocked per frame.
     *
     * The transfer rides the established MessagePort (not the worker global
     * `postMessage`) so it is multiplexed with the rest of the host ↔ worker
     * traffic on the same channel. The `OffscreenCanvas` handle is the sole
     * `Transferable` in the envelope; no capability token, MCP context, or
     * other host-only data crosses the boundary.
     *
     * Throws if the environment lacks `transferControlToOffscreen()` or if the
     * canvas has already been transferred — see {@link createOffscreenCanvasTransfer}
     * for the exact diagnostics.
     */
    public transferCanvas(
        canvas: HTMLCanvasElement,
        options: { surfaceId: string },
    ): { offscreen: OffscreenCanvas }
    {
        return createOffscreenCanvasTransfer({
            canvas,
            surfaceId: options.surfaceId,
            postMessage: (message, transfer) =>
            {
                this.hostPort.postMessage(message, transfer);
            },
        });
    }

    /**
     * Forward a host-sourced input event to the worker over the port. The
     * payload shape is opaque to the transport — pointer / wheel / keyboard
     * envelopes share the same wire type. Callers are expected to wrap
     * high-frequency (pointer-move, wheel, scroll) events in
     * {@link createCoalescer} or {@link createInputEventCoalescer} BEFORE
     * calling this method so the port never sees the un-coalesced flood.
     *
     * Keyboard / pointer-down / pointer-up events are discrete and should be
     * delivered without coalescing — pass them straight through.
     */
    public postInputEvent(payload: InputEventPayload): void
    {
        this.hostPort.postMessage({
            type: "ethisys:input:event",
            payload,
        });
    }

    /**
     * Build a trailing-edge coalescer keyed to {@link WorkerRemoteDomTransportOptions.coalesceMs}.
     *
     * Use it to wrap pointer-move / scroll / resize callbacks **before**
     * they cross the port. The last payload in any coalescing window is the
     * one that wins.
     */
    public createCoalescer<T>(consumer: (payload: T) => void): (payload: T) => void
    {
        let pending: T | undefined;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const flush = () =>
        {
            timer = undefined;
            const value = pending;
            pending = undefined;
            if (value !== undefined)
            {
                consumer(value);
            }
        };
        return (payload: T) =>
        {
            pending = payload;
            if (timer === undefined)
            {
                timer = setTimeout(flush, this.coalesceMs);
            }
        };
    }

    // ─── Bridge push methods (WI 4858 sub-plan 2) ─────────────────────────────

    /**
     * Register a consumer for inbound plugin→host bridge messages
     * (chrome requests, a11y announce, lifecycle events). Only the most recently
     * registered consumer is kept — the host mount wires exactly one.
     */
    public onBridgeMessage(consumer: (msg: unknown) => void): void
    {
        this.bridgeMessageConsumer = consumer;
    }

    /**
     * Push the current host theme to the plugin worker. Safe to call on every
     * host theme-context change — the transport coalesces nothing here; rate
     * limiting at the call site is the host's responsibility.
     */
    public pushTheme(payload: Omit<BridgePushThemeEnvelope, "type">): void
    {
        this.safePostMessage({ type: BRIDGE_PUSH_THEME, ...payload });
    }

    /** Push the current host locale and text direction to the plugin worker. */
    public pushLocale(payload: Omit<BridgePushLocaleEnvelope, "type">): void
    {
        this.safePostMessage({ type: BRIDGE_PUSH_LOCALE, ...payload });
    }

    /** Push the current UI density preference to the plugin worker. */
    public pushDensity(payload: Omit<BridgePushDensityEnvelope, "type">): void
    {
        this.safePostMessage({ type: BRIDGE_PUSH_DENSITY, ...payload });
    }

    /** Push updated accessibility preferences to the plugin worker. */
    public pushA11y(payload: Omit<BridgePushA11yEnvelope, "type">): void
    {
        this.safePostMessage({ type: BRIDGE_PUSH_A11Y, ...payload });
    }

    /** Push the current SPA navigation state to the plugin worker. */
    public pushNav(payload: Omit<BridgeNavPushEnvelope, "type">): void
    {
        this.safePostMessage({ type: BRIDGE_NAV_PUSH, ...payload });
    }

    /**
     * Tear down the worker and port. Idempotent.
     *
     * Order matters: we abort BEFORE closing the port so any in-flight
     * `mcpClient.fetch` that observes the signal short-circuits and the
     * subsequent attempt to post a reply lands in the disposed-guard branch
     * (which silently drops the post) instead of throwing on a closed port.
     */
    public dispose(): void
    {
        if (this.disposed)
        {
            return;
        }
        this.disposed = true;
        try
        {
            this.abortController.abort();
            // Fan out to every in-flight per-request controller so their
            // `mcpClient.fetch` calls short-circuit too — the dispose-level
            // controller is not the signal those requests were handed.
            for (const controller of this.inFlightAborts.values())
            {
                controller.abort();
            }
            this.inFlightAborts.clear();
        }
        catch
        {
            // AbortController.abort() shouldn't throw, but never let dispose
            // crash the caller — teardown must be best-effort across all
            // resources.
        }
        try
        {
            this.hostPort.close();
        }
        catch
        {
            // ignore — port may already be closed
        }
        try
        {
            this.worker.terminate();
        }
        catch
        {
            // ignore
        }
    }

    /**
     * Best-effort wrapper around `hostPort.postMessage` that tolerates posts
     * arriving after {@link dispose}. The browser throws on a closed port and
     * the async handlers below can race dispose, so any post initiated by an
     * awaited continuation must be guarded.
     */
    private safePostMessage(message: unknown): void
    {
        if (this.disposed)
        {
            return;
        }
        try
        {
            this.hostPort.postMessage(message);
        }
        catch
        {
            // Port may have been closed between the disposed check and the
            // post call (browser closes ports asynchronously). Swallowing the
            // throw here is safe because we are already on a teardown path.
        }
    }

    // ─── Port message handling ──────────────────────────────────────────────

    private handlePortMessage(message: InboundMessage): void
    {
        // Post-dispose port traffic is dropped silently. The browser closes
        // MessagePorts asynchronously; any handler invocation after dispose
        // is a race we observe-and-skip rather than try to service.
        if (this.disposed)
        {
            return;
        }
        switch (message.type)
        {
        case "ethisys:mcp:invokeTool":
            this.dispatchMcp(message as InvokeToolMessage, "ethisys:mcp:invokeTool:result", (m) => this.handleInvokeTool(m));
            return;

        case "ethisys:mcp:getResource":
            this.dispatchMcp(message as GetResourceMessage, "ethisys:mcp:getResource:result", (m) => this.handleGetResource(m));
            return;

        case "ethisys:mcp:uploadDocument":
            this.dispatchMcp(message as UploadDocumentMessage, "ethisys:mcp:uploadDocument:result", (m) => this.handleUploadDocument(m));
            return;

        case "ethisys:mcp:abort":
            // Client-side cancel (hook unmount / arg change). Abort just the
            // matching in-flight request so its `mcpClient.fetch` short-circuits
            // instead of streaming to completion. The worker has already dropped
            // its pending resolver, so any late `:result` reply is ignored.
            this.inFlightAborts.get((message as { id?: string }).id ?? "")?.abort();
            return;

        case "ethisys:remotedom":
            this.remoteDomConsumer?.((message as RemoteDomMessage).payload);
            return;

        case BRIDGE_CHROME_REQUEST:
        case BRIDGE_A11Y_ANNOUNCE:
        case BRIDGE_LIFECYCLE_EVENT:
            this.bridgeMessageConsumer?.(message);
            return;

        default:
            // Unknown messages are ignored. The host MUST NOT echo unknown
            // shapes back to the worker — that would create an attacker-
            // controlled reflection surface.
            return;
        }
    }

    /**
     * Gate inbound MCP requests through the bounded concurrency window,
     * reject with a deterministic error reply when the cap is exceeded, and
     * decrement the counter unconditionally when the underlying handler
     * settles (regardless of resolution/rejection shape).
     */
    private dispatchMcp<T extends { id: string }>(
        message: T,
        resultType: string,
        handler: (m: T) => Promise<void>,
    ): void
    {
        if (this.inFlightMcpRequests >= this.maxConcurrentMcpRequests)
        {
            // Back-pressure boundary. The worker MUST surface this to plugin
            // code so it can retry / degrade gracefully rather than silently
            // hang on a never-resolving promise.
            this.safePostMessage({
                id: message.id,
                type: resultType,
                ok: false,
                error: `MCP back-pressure: in-flight cap of ${this.maxConcurrentMcpRequests} reached.`,
            });
            return;
        }
        this.inFlightMcpRequests++;
        this.registerRequestAbort(message.id);
        handler(message).finally(() =>
        {
            this.releaseRequestAbort(message.id);
            this.inFlightMcpRequests = Math.max(0, this.inFlightMcpRequests - 1);
        });
    }

    /**
     * Create a per-request AbortController and register it under the request id
     * so an inbound `ethisys:mcp:abort` can cancel just this call. If the
     * transport is already disposed the controller starts aborted so the
     * handler's `mcpClient.fetch` short-circuits immediately.
     */
    private registerRequestAbort(id: string): void
    {
        const controller = new AbortController();
        if (this.abortController.signal.aborted)
        {
            controller.abort();
        }
        this.inFlightAborts.set(id, controller);
    }

    /** Drop a settled request's AbortController. */
    private releaseRequestAbort(id: string): void
    {
        this.inFlightAborts.delete(id);
    }

    /**
     * The signal to hand to `mcpClient.fetch` for a given request: the
     * per-request controller when registered (client cancel path), falling back
     * to the dispose-level controller.
     */
    private requestSignal(id: string): AbortSignal
    {
        return this.inFlightAborts.get(id)?.signal ?? this.abortController.signal;
    }

    /**
     * Raises the skew callback the first time this surface is seen to be running against a version
     * that is no longer installed.
     *
     * Latched deliberately. A page typically has several MCP calls in flight, and after an upgrade
     * every one of them reports the same skew — firing per response would ask the host to remount
     * the same surface repeatedly, and the second remount would tear down the first. One signal per
     * transport is all a remount needs, and the transport is discarded along with the surface.
     *
     * The callback is host code, so it is assumed to be able to throw. Two consequences are guarded
     * here: the throw must not escape into the MCP handler's catch (which would reply `ok: false`
     * for a call that actually succeeded), and it must not consume the latch (which would leave the
     * surface with no response AND no further chance of being told to remount).
     */
    private reportVersionSkew(response: McpHttpResponse): void
    {
        if (this.versionSkewReported || this.onVersionSkew === undefined)
        {
            return;
        }
        const skew = detectVersionSkew(response, this.mountedExtensionVersion);
        if (skew === null)
        {
            return;
        }
        try
        {
            this.onVersionSkew(skew);
            this.versionSkewReported = true;
        }
        catch
        {
            // Swallowed on purpose: a failed remount attempt is the host's problem, and turning it
            // into an MCP failure would corrupt a response the plugin is entitled to. The latch
            // stays open so the next response gets another chance to raise it.
        }
    }

    private async handleInvokeTool(message: InvokeToolMessage): Promise<void>
    {
        let token: string;
        try
        {
            token = await this.capabilityTokenProvider();
        }
        catch (err)
        {
            this.replyError(message.id, "ethisys:mcp:invokeTool:result", err);
            return;
        }
        // Disposed-before-token: short-circuit. The token mint may have
        // resolved seconds after dispose; posting the reply now would race
        // a closed port.
        if (this.disposed)
        {
            return;
        }
        try
        {
            const result = await this.mcpClient.fetch({
                kind: "invokeTool",
                name: message.name,
                args: message.args,
                capabilityToken: token,
                signal: this.requestSignal(message.id),
            });
            this.reportVersionSkew(result);
            // CRITICAL: response payload must never include the token. We
            // forward ONLY the public-facing fields the plugin needs.
            this.safePostMessage({
                id: message.id,
                type: "ethisys:mcp:invokeTool:result",
                ok: result.ok,
                data: result.data,
                error: result.error,
                // A client that REPORTS failure rather than throwing lands here, which is the common
                // shape for anything wrapping HTTP. Classifying only the thrown path left these
                // arriving as `internal`.
                code: classifyHostResponse(result),
            });
        }
        catch (err)
        {
            this.replyError(message.id, "ethisys:mcp:invokeTool:result", err);
        }
    }

    private async handleGetResource(message: GetResourceMessage): Promise<void>
    {
        let token: string;
        try
        {
            token = await this.capabilityTokenProvider();
        }
        catch (err)
        {
            this.replyError(message.id, "ethisys:mcp:getResource:result", err);
            return;
        }
        if (this.disposed)
        {
            return;
        }
        try
        {
            const result = await this.mcpClient.fetch({
                kind: "getResource",
                uri: message.uri,
                capabilityToken: token,
                signal: this.requestSignal(message.id),
            });
            this.reportVersionSkew(result);
            this.safePostMessage({
                id: message.id,
                type: "ethisys:mcp:getResource:result",
                ok: result.ok,
                data: result.data,
                error: result.error,
                // A client that REPORTS failure rather than throwing lands here, which is the common
                // shape for anything wrapping HTTP. Classifying only the thrown path left these
                // arriving as `internal`.
                code: classifyHostResponse(result),
            });
        }
        catch (err)
        {
            this.replyError(message.id, "ethisys:mcp:getResource:result", err);
        }
    }

    private async handleUploadDocument(message: UploadDocumentMessage): Promise<void>
    {
        let token: string;
        try
        {
            token = await this.capabilityTokenProvider();
        }
        catch (err)
        {
            this.replyError(message.id, "ethisys:mcp:uploadDocument:result", err);
            return;
        }
        // Disposed-before-token: short-circuit. The token mint may have resolved
        // after dispose; posting the reply now would race a closed port.
        if (this.disposed)
        {
            return;
        }
        try
        {
            // CRITICAL security invariant: the worker transferred ONLY bytes +
            // metadata. The capability token is minted + attached here (host
            // side) and never crosses the port. The abort signal propagates so
            // a client cancel / host dispose aborts the octet-stream upload.
            const result = await this.mcpClient.fetch({
                kind: "uploadDocument",
                meta: message.meta,
                buffer: message.buffer,
                capabilityToken: token,
                signal: this.requestSignal(message.id),
            });
            this.reportVersionSkew(result);
            // Response payload must never include the token — forward only the
            // public-facing UploadDocumentResult fields.
            this.safePostMessage({
                id: message.id,
                type: "ethisys:mcp:uploadDocument:result",
                ok: result.ok,
                data: result.data,
                error: result.error,
                // A client that REPORTS failure rather than throwing lands here, which is the common
                // shape for anything wrapping HTTP. Classifying only the thrown path left these
                // arriving as `internal`.
                code: classifyHostResponse(result),
            });
        }
        catch (err)
        {
            this.replyError(message.id, "ethisys:mcp:uploadDocument:result", err);
        }
    }

    private replyError(id: string, type: string, err: unknown): void
    {
        // Surface a sanitised error string — never the raw error object
        // (which may carry stack traces or token fragments from a misbehaving
        // mcpClient implementation). Routed through safePostMessage so a
        // post-dispose reply does not crash on a closed port.
        const message = err instanceof Error ? err.message : "MCP request failed";
        // `code` is derived from the error's own explicit classification (or an HTTP status), never
        // from its message — see classifyHostError. It is safe to expose where the message may not
        // be: a closed vocabulary carries no host internals.
        this.safePostMessage({ id, type, ok: false, error: message, code: classifyHostError(err) });
    }
}
